import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BUILDING_TYPES_BY_USAGE, DEFAULT_BUILDING_TYPES, HEATING_TYPES } from './categories';

interface Building {
  id: string;
  name: string;
  buildingType: string;
  yearBuilt: number;
  grossFloorArea: number;
  heatingType: string;
  description: string;
  navvisUrl: string;
  cafmUrl: string;
  files: any[];
}

interface Plot {
  id: string;
  name: string;
  parcelNumber: string;
  areaM2: number;
  description: string;
  buildings: Building[];
  files: any[];
}

interface Project {
  id: string;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  usageType: string;
  usageSubType: string;
  owner: string;
  description: string;
  latitude: number;
  longitude: number;
  plots: Plot[];
}

type DeleteTarget = { type: 'plot'; item: Plot } | { type: 'building'; item: Building } | null;

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [expandedPlots, setExpandedPlots] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteTarget>(null);

  const [showPlotForm, setShowPlotForm] = useState(false);
  const [editPlot, setEditPlot] = useState<Plot | null>(null);
  const [plotName, setPlotName] = useState('');
  const [plotParcel, setPlotParcel] = useState('');
  const [plotArea, setPlotArea] = useState('');
  const [plotDesc, setPlotDesc] = useState('');

  const [showBuildingForm, setShowBuildingForm] = useState<string | null>(null);
  const [editBuilding, setEditBuilding] = useState<Building | null>(null);
  const [buildingName, setBuildingName] = useState('');
  const [buildingType, setBuildingType] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [grossFloorArea, setGrossFloorArea] = useState('');
  const [heatingType, setHeatingType] = useState('');
  const [buildingDesc, setBuildingDesc] = useState('');
  const [navvisUrl, setNavvisUrl] = useState('');
  const [cafmUrl, setCafmUrl] = useState('');

  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canWrite = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(user.role);
  const canDelete = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(user.role);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
      const plotIds = new Set<string>(response.data.plots?.map((p: Plot) => p.id) || []);
      setExpandedPlots(plotIds);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchProject(); }, [id]); // eslint-disable-line

  const buildingTypes = project?.usageType
    ? (BUILDING_TYPES_BY_USAGE[project.usageType] || DEFAULT_BUILDING_TYPES)
    : DEFAULT_BUILDING_TYPES;

  // ─── Plot handlers ──────────────────────────────────────────────────────────

  const openCreatePlot = () => {
    setEditPlot(null);
    setPlotName(''); setPlotParcel(''); setPlotArea(''); setPlotDesc('');
    setShowPlotForm(true);
  };

  const openEditPlot = (plot: Plot) => {
    setEditPlot(plot);
    setPlotName(plot.name);
    setPlotParcel(plot.parcelNumber || '');
    setPlotArea(plot.areaM2 ? String(plot.areaM2) : '');
    setPlotDesc(plot.description || '');
    setShowPlotForm(true);
  };

  const handleSavePlot = async () => {
    if (!plotName) return;
    setLoading(true);
    try {
      if (editPlot) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/plots/${editPlot.id}`, {
          name: plotName, parcelNumber: plotParcel,
          areaM2: plotArea ? parseFloat(plotArea) : undefined,
          description: plotDesc
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/plots`, {
          name: plotName, parcelNumber: plotParcel,
          areaM2: plotArea ? parseFloat(plotArea) : undefined,
          description: plotDesc, projectId: id
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowPlotForm(false); setEditPlot(null);
      setPlotName(''); setPlotParcel(''); setPlotArea(''); setPlotDesc('');
      fetchProject();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Building handlers ──────────────────────────────────────────────────────

  const openCreateBuilding = (plotId: string) => {
    setEditBuilding(null);
    setBuildingName(''); setBuildingType(''); setYearBuilt('');
    setGrossFloorArea(''); setHeatingType(''); setBuildingDesc('');
    setNavvisUrl(''); setCafmUrl('');
    setShowBuildingForm(plotId);
  };

  const openEditBuilding = (building: Building, plotId: string) => {
    setEditBuilding(building);
    setBuildingName(building.name);
    setBuildingType(building.buildingType || '');
    setYearBuilt(building.yearBuilt ? String(building.yearBuilt) : '');
    setGrossFloorArea(building.grossFloorArea ? String(building.grossFloorArea) : '');
    setHeatingType(building.heatingType || '');
    setBuildingDesc(building.description || '');
    setNavvisUrl(building.navvisUrl || '');
    setCafmUrl(building.cafmUrl || '');
    setShowBuildingForm(plotId);
  };

  const handleSaveBuilding = async (plotId: string) => {
    if (!buildingName) return;
    setLoading(true);
    try {
      if (editBuilding) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/buildings/${editBuilding.id}`, {
          name: buildingName, buildingType,
          yearBuilt: yearBuilt ? parseInt(yearBuilt) : undefined,
          grossFloorArea: grossFloorArea ? parseFloat(grossFloorArea) : undefined,
          heatingType, description: buildingDesc,
          navvisUrl: navvisUrl || null,
          cafmUrl: cafmUrl || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/buildings`, {
          name: buildingName, buildingType,
          yearBuilt: yearBuilt ? parseInt(yearBuilt) : undefined,
          grossFloorArea: grossFloorArea ? parseFloat(grossFloorArea) : undefined,
          heatingType, description: buildingDesc,
          navvisUrl: navvisUrl || null,
          cafmUrl: cafmUrl || null,
          plotId
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowBuildingForm(null); setEditBuilding(null);
      setBuildingName(''); setBuildingType(''); setYearBuilt('');
      setGrossFloorArea(''); setHeatingType(''); setBuildingDesc('');
      setNavvisUrl(''); setCafmUrl('');
      fetchProject();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Delete handler ─────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'plot') {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/plots/${deleteConfirm.item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/buildings/${deleteConfirm.item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setDeleteConfirm(null);
      fetchProject();
    } catch (err) {
      console.error(err);
    }
  };

  const togglePlot = (plotId: string) => {
    setExpandedPlots(prev => {
      const next = new Set(prev);
      next.has(plotId) ? next.delete(plotId) : next.add(plotId);
      return next;
    });
  };

  const totalBuildings = project?.plots?.reduce((sum, p) => sum + (p.buildings?.length || 0), 0) || 0;
  const totalFiles = project?.plots?.reduce((sum, p) =>
    sum + (p.files?.length || 0) + p.buildings.reduce((s, b) => s + (b.files?.length || 0), 0), 0) || 0;

  if (!project) return <div style={s.loading}>Laden...</div>;

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a href="/projects" style={s.navItemActive}>Liegenschaften</a>
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN'].includes(user.role) && (
            <a href="/users" style={s.navItem}>Benutzer</a>
          )}
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role) && (
            <a href="/customers" style={s.navItem}>Kunden</a>
          )}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>Abmelden</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.breadcrumb}>
          <a href="/projects" style={s.breadcrumbLink}>Liegenschaften</a>
          <span style={s.breadcrumbSep}> › </span>
          <span style={s.breadcrumbCurrent}>{project.name}</span>
        </div>

        <div style={s.header}>
          <div>
            <h1 style={s.title}>{project.name}</h1>
            <p style={s.subtitle}>📍 {project.street}, {project.zipCode} {project.city}</p>
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {project.usageType && <span style={s.badge}>{project.usageType}</span>}
              {project.usageSubType && <span style={s.badgeSub}>{project.usageSubType}</span>}
            </div>
            {project.owner && <p style={s.owner}>🏛️ {project.owner}</p>}
          </div>
          {canWrite && (
            <button style={s.addButton} onClick={openCreatePlot}>+ Neues Grundstück</button>
          )}
        </div>

        <div style={s.stats}>
          {[
            { num: project.plots?.length || 0, label: 'Grundstücke' },
            { num: totalBuildings, label: 'Gebäude' },
            { num: totalFiles, label: 'Dateien' },
          ].map(({ num, label }) => (
            <div key={label} style={s.statCard}>
              <span style={s.statNumber}>{num}</span>
              <span style={s.statLabel}>{label}</span>
            </div>
          ))}
        </div>

        {/* Plot Form */}
        {showPlotForm && (
          <div style={s.form}>
            <h3 style={s.formTitle}>{editPlot ? 'Grundstück bearbeiten' : 'Neues Grundstück anlegen'}</h3>
            <div style={s.formGrid}>
              <div style={s.inputGroup}>
                <label style={s.label}>Name *</label>
                <input style={s.input} value={plotName} onChange={e => setPlotName(e.target.value)} placeholder="z.B. Hauptgrundstück" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Flurstücknummer <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                <input style={s.input} value={plotParcel} onChange={e => setPlotParcel(e.target.value)} placeholder="z.B. 053066-43-321" />
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#aaa' }}>Format: Gemarkung-Flur-Nummer (z.B. 053066-43-321)</p>
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Fläche (m²)</label>
                <input style={s.input} type="number" value={plotArea} onChange={e => setPlotArea(e.target.value)} placeholder="z.B. 12500" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Beschreibung</label>
                <input style={s.input} value={plotDesc} onChange={e => setPlotDesc(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div style={s.formButtons}>
              <button style={s.cancelButton} onClick={() => { setShowPlotForm(false); setEditPlot(null); }}>Abbrechen</button>
              <button style={{ ...s.saveButton, opacity: loading ? 0.7 : 1 }} onClick={handleSavePlot} disabled={loading}>
                {loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {project.plots?.length === 0 ? (
          <div style={s.empty}><p>Noch keine Grundstücke angelegt.</p></div>
        ) : (
          project.plots?.map(plot => (
            <div key={plot.id} style={s.plotCard}>
              <div style={s.plotHeader} onClick={() => togglePlot(plot.id)}>
                <div style={s.plotHeaderLeft}>
                  <span style={s.plotToggle}>{expandedPlots.has(plot.id) ? '▼' : '▶'}</span>
                  <div>
                    <h3 style={s.plotTitle}>🏞️ {plot.name}</h3>
                    <div style={s.plotMeta}>
                      {plot.parcelNumber && <span>Flurstück: {plot.parcelNumber}</span>}
                      {plot.areaM2 && <span> · {plot.areaM2.toLocaleString('de-DE')} m²</span>}
                    </div>
                  </div>
                </div>
                <div style={s.plotHeaderRight}>
                  <span style={s.plotBadge}>{plot.buildings.length} Gebäude</span>
                  {canWrite && (
                    <button style={s.addBuildingBtn} onClick={e => { e.stopPropagation(); openCreateBuilding(plot.id); }}>+ Gebäude</button>
                  )}
                  {canWrite && (
                    <button style={s.iconBtn} title="Bearbeiten" onClick={e => { e.stopPropagation(); openEditPlot(plot); }}>✏️</button>
                  )}
                  {canDelete && (
                    <button style={s.iconBtn} title="Löschen" onClick={e => { e.stopPropagation(); setDeleteConfirm({ type: 'plot', item: plot }); }}>🗑️</button>
                  )}
                  <button style={s.plotDetailBtn} onClick={e => { e.stopPropagation(); window.location.href = `/plots/${plot.id}`; }}>Details →</button>
                </div>
              </div>

              {/* Building Form */}
              {showBuildingForm === plot.id && (
                <div style={s.buildingForm}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#0f2744' }}>
                    {editBuilding ? `${editBuilding.name} bearbeiten` : `Neues Gebäude in ${plot.name}`}
                  </h4>
                  <div style={s.formGrid}>
                    <div style={s.inputGroup}>
                      <label style={s.label}>Name *</label>
                      <input style={s.input} value={buildingName} onChange={e => setBuildingName(e.target.value)} placeholder="z.B. Hauptgebäude" />
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>Gebäudetyp</label>
                      <select style={s.input} value={buildingType} onChange={e => setBuildingType(e.target.value)}>
                        <option value="">Bitte wählen...</option>
                        {buildingTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>Baujahr</label>
                      <input style={s.input} type="number" value={yearBuilt} onChange={e => setYearBuilt(e.target.value)} placeholder="z.B. 1975" />
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>BGF (m²)</label>
                      <input style={s.input} type="number" value={grossFloorArea} onChange={e => setGrossFloorArea(e.target.value)} placeholder="z.B. 4200" />
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>Heizungsart</label>
                      <select style={s.input} value={heatingType} onChange={e => setHeatingType(e.target.value)}>
                        <option value="">Bitte wählen...</option>
                        {HEATING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>Beschreibung</label>
                      <input style={s.input} value={buildingDesc} onChange={e => setBuildingDesc(e.target.value)} placeholder="Optional" />
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>NavVis Link <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                      <input style={s.input} value={navvisUrl} onChange={e => setNavvisUrl(e.target.value)} placeholder="https://..." />
                    </div>
                    <div style={s.inputGroup}>
                      <label style={s.label}>CAFM Link <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                      <input style={s.input} value={cafmUrl} onChange={e => setCafmUrl(e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                  <div style={s.formButtons}>
                    <button style={s.cancelButton} onClick={() => { setShowBuildingForm(null); setEditBuilding(null); }}>Abbrechen</button>
                    <button style={{ ...s.saveButton, opacity: loading ? 0.7 : 1 }} onClick={() => handleSaveBuilding(plot.id)} disabled={loading}>
                      {loading ? 'Speichern...' : 'Speichern'}
                    </button>
                  </div>
                </div>
              )}

              {/* Buildings */}
              {expandedPlots.has(plot.id) && (
                <div style={s.buildingGrid}>
                  {plot.buildings.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: '13px', padding: '16px' }}>Noch keine Gebäude angelegt.</p>
                  ) : (
                    plot.buildings.map(building => (
                      <div key={building.id} style={s.buildingCard}>
                        <div style={s.buildingHeader}>
                          <h4 style={s.buildingTitle} onClick={() => window.location.href = `/buildings/${building.id}`}>
                            {building.name}
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={s.fileCount}>{building.files?.length || 0} Dateien</span>
                            {canWrite && (
                              <button style={s.iconBtn} title="Bearbeiten" onClick={() => openEditBuilding(building, plot.id)}>✏️</button>
                            )}
                            {canDelete && (
                              <button style={s.iconBtn} title="Löschen" onClick={() => setDeleteConfirm({ type: 'building', item: building })}>🗑️</button>
                            )}
                          </div>
                        </div>
                        <div style={s.buildingMeta}>
                          {building.buildingType && <span style={s.metaTag}>{building.buildingType}</span>}
                          {building.yearBuilt && <span style={s.metaTag}>Bj. {building.yearBuilt}</span>}
                          {building.grossFloorArea && <span style={s.metaTag}>{building.grossFloorArea.toLocaleString('de-DE')} m²</span>}
                        </div>
                        <div style={s.dataTags}>
                          {[
                            { key: 'POINTCLOUD', label: 'PW' },
                            { key: 'MODEL_3D', label: '3D' },
                            { key: 'DRAWING_2D', label: '2D' },
                            { key: 'DOCUMENT', label: 'DO' },
                          ].map(({ key, label }) => (
                            <span key={key} style={building.files?.some((f: any) => f.mainCategory === key) ? s.tagActive : s.tag}>
                              {label}
                            </span>
                          ))}
                        </div>
                        {/* NavVis / CAFM Links */}
                        {(building.navvisUrl || building.cafmUrl) && (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                            {building.navvisUrl && (
                              <a href={building.navvisUrl} target="_blank" rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{ fontSize: '11px', color: '#3498db', textDecoration: 'none', padding: '2px 8px', border: '1px solid #3498db', borderRadius: '4px' }}>
                                NavVis
                              </a>
                            )}
                            {building.cafmUrl && (
                              <a href={building.cafmUrl} target="_blank" rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{ fontSize: '11px', color: '#9b59b6', textDecoration: 'none', padding: '2px 8px', border: '1px solid #9b59b6', borderRadius: '4px' }}>
                                CAFM
                              </a>
                            )}
                          </div>
                        )}
                        <button style={s.openBuildingBtn} onClick={() => window.location.href = `/buildings/${building.id}`}>
                          Öffnen →
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Modal */}
      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={s.modalIcon}>🗑️</p>
            <p style={s.modalTitle}>{deleteConfirm.type === 'plot' ? 'Grundstück' : 'Gebäude'} löschen</p>
            <p style={s.modalText}>
              Möchtest du <strong>{deleteConfirm.item.name}</strong> wirklich löschen?
              {deleteConfirm.type === 'plot' && ' Alle zugehörigen Gebäude und Dateien werden ebenfalls gelöscht.'}
              {deleteConfirm.type === 'building' && ' Alle zugehörigen Dateien werden ebenfalls gelöscht.'}
            </p>
            <div style={s.modalButtons}>
              <button style={s.cancelButton} onClick={() => setDeleteConfirm(null)}>Abbrechen</button>
              <button style={s.deleteButton} onClick={handleDelete}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#888' },
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', textDecoration: 'none', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '32px', overflow: 'auto' },
  breadcrumb: { marginBottom: '16px', fontSize: '14px' },
  breadcrumbLink: { color: '#00c49a', textDecoration: 'none' },
  breadcrumbSep: { color: '#aaa', margin: '0 8px' },
  breadcrumbCurrent: { color: '#666' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', color: '#0f2744', margin: '0 0 4px 0' },
  subtitle: { color: '#888', margin: '0', fontSize: '14px' },
  badge: { backgroundColor: '#f0f4ff', color: '#3498db', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  badgeSub: { backgroundColor: '#fff0f0', color: '#e74c3c', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
  owner: { color: '#888', fontSize: '13px', margin: '6px 0 0 0' },
  addButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', flexShrink: 0 },
  stats: { display: 'flex', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '16px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  statNumber: { fontSize: '28px', fontWeight: 'bold', color: '#00c49a' },
  statLabel: { fontSize: '12px', color: '#888' },
  form: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 20px 0', color: '#0f2744' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#333' },
  input: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelButton: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  empty: { backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', color: '#aaa', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  plotCard: { backgroundColor: 'white', borderRadius: '12px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  plotHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' },
  plotHeaderLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  plotToggle: { color: '#aaa', fontSize: '12px', width: '16px' },
  plotTitle: { margin: '0 0 2px 0', color: '#0f2744', fontSize: '15px' },
  plotMeta: { color: '#888', fontSize: '12px' },
  plotHeaderRight: { display: 'flex', alignItems: 'center', gap: '6px' },
  plotBadge: { backgroundColor: '#f0faf7', color: '#00c49a', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  addBuildingBtn: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px 4px', borderRadius: '4px' },
  plotDetailBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' },
  buildingForm: { padding: '20px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #f0f0f0' },
  buildingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px', padding: '16px' },
  buildingCard: { backgroundColor: '#f9f9f9', borderRadius: '8px', padding: '16px', border: '1px solid #f0f0f0' },
  buildingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  buildingTitle: { margin: '0', color: '#0f2744', fontSize: '14px', cursor: 'pointer' },
  fileCount: { backgroundColor: '#e8f5f0', color: '#00c49a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' },
  buildingMeta: { display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' },
  metaTag: { backgroundColor: '#f0f0f0', color: '#666', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' },
  dataTags: { display: 'flex', gap: '4px', marginBottom: '10px' },
  tag: { padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f0f0f0', color: '#ccc' },
  tagActive: { padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: '#00c49a', color: 'white' },
  openBuildingBtn: { backgroundColor: 'transparent', border: '1px solid #00c49a', color: '#00c49a', padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center' },
  modalIcon: { fontSize: '32px', margin: '0 0 8px 0' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' },
  modalText: { color: '#666', fontSize: '14px', margin: '0 0 24px 0', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
};

export default ProjectDetail;
